export const anatomyModule = {
  id: "anatomy",
  sections: [
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Thyroid Anatomy</h3>
      <p>The thyroid gland is a butterfly-shaped gland located in the <b>front of the neck</b>, 
      just below the larynx. It consists of two lobes, one on each side of the trachea, 
      connected by a thin piece of tissue called the <strong>isthmus.</strong></p> 
      <p>As the thyroid gland is formed during embryologic development, it
       moves from the base of the tongue to its final position in the neck via the <span class="glossary-term">thyroglossal duct<span class="tooltip">
       is a temporary embryological canal, that acts as a pathway for the thyroid gland to descend during
       embryological development. It starts at the base of the tongue and ends in the neck at the location of the future thyroid gland
        It usually disappears once the thyroid reaches its final position in front of the neck.</span></span>.</p>
       <p>Because of this migration, the thyroid gland can sometimes be found in other locations, 
        such as higher up in the neck, at the base of the tongue, or even in the chest, known as an <span class="glossary-term">ectopic<span class="tooltip">An ectopic mass is mass located outside its normal anatomical position, often due to abnormal migration during development.</span></span> thyroid gland.</p>
        <p>Most often you end up with a small strip of thyroid gland extending superiorly from the 
        isthmus, also called the <strong>pyramidal lobe.</strong></p>
      <p>The thyroid gland is responsible for producing hormones that regulate metabolism,
       growth, and development.</p>
      <div class="highlight-box">Key anatomical features of the thyroid gland:
       <ul>
        <li>Right lobe</li>
        <li>Left lobe</li>
        <li>Isthmus: <em>the lobe that connects the right and left lobe.</em></li>
        <li>Pyramidal lobe: <em>normal anatomical variant & remnant of the thyroglossal duct.</em></li>
       </ul></div>
      `,
      image: "/images/thyroid/Thyroidanatomy1.png",
    },
    {
      type: "question",
      text: "What is the pyramidal lobe a remnant of?",
      options: ["Thyroglossal duct", "Parathyroid gland", "Larynx", "Trachea"],
      correct: 0,
      explanation: "The pyramidal lobe is a remnant of the thyroglossal duct.",
      image: "/images/thyroid/Thyroidanatomy1.png",
    },
    {
      type: "question",
      text: "What is the name of this section of the thyroid gland?",
      options: ["Thyroglossal duct", "Pyramidal lobe", "Isthmus", "Larynx"],
      correct: 2,
      explanation:
        "The isthmus is the narrow strip of tissue that connects the right and left lobes of the thyroid gland.",
      image: "/images/thyroid/thyroidquiz1.png",
    },
    {
      type: "question",
      text: "What is the name of this section of the thyroid gland?",
      options: ["Thyroglossal duct", "Pyramidal lobe", "Isthmus", "Larynx"],
      correct: 1,
      explanation: "The pyramidal lobe is a remnant of the thyroglossal duct.",
      image: "/images/thyroid/thyroidquiz2.png",
    },
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Thyroid Arteries</h3>
      <p>The thyroid gland is supplied by two main arteries: </p>       
      <p>
      1. The <strong>superior thyroid artery</strong> is a branch of the external carotid artery. It descends to the thyroid gland, supplying blood to the upper part of the gland, including the superior poles of both lobes and the isthmus.
      </p>
      <p>
      2. The <strong>inferior thyroid artery</strong> arises from the thyrocervical trunk, a branch of the subclavian artery. It ascends to the thyroid gland, supplying blood to the lower part of the gland, including the inferior poles of both lobes.
      </p>
      <p>
      In some individuals, there may also be a third artery called the <strong>thyroid ima artery</strong>, which arises from the brachiocephalic trunk or the aortic arch. This artery is less common and supplies blood to the isthmus and lower parts of the thyroid gland.
      </p>
      <p>
      The thyroid gland has a rich blood supply, which is important for its function in producing and releasing hormones into the bloodstream.
      </p>

      <div class="mnemonic-card">
        <div class="mnemonic-title">Thyroid gland arteries</div>
      <div class="mnemonic-content">
      <ul>
      <li>External carotid artery => <b class="purple-term">Superior thyroid artery</b> => Superior pole and isthmus</li>
      <li>Subclavian artery (thyrocervical trunk) => <b class="purple-term">Inferior thyroid artery</b> => Inferior pole</li>
      <li>Brachiocephalic trunk or aortic arch => <b class="purple-term">Thyroid ima artery</b> (in some individuals) => Isthmus and lower parts</li>
          </ul>
        </div></div>
        `,
      image: "/images/thyroid/thyroidarteries.png",
    },
    {
      type: "question",
      text: "Which artery supplies blood to the superior pole of the thyroid gland?",
      options: [
        "superior thyroid artery",
        "inferior thyroid artery",
        "thyroid ima artery",
        "brachiocephalic artery ",
      ],
      correct: 0,
      explanation: "You smart person <3",
      explanationImage: "/images/thyroid/thyroidarteries.png",
    },
    {
      type: "question",
      text: "Which artery arises from the subclavian artery (thyrocervical trunk)?",
      options: [
        "superior thyroid artery",
        "inferior thyroid artery",
        "thyroid ima artery",
        "external carotid artery ",
      ],
      correct: 1,
      explanation:
        "The inferior thyroid artery arises from the subclavian artery (thyrocervical trunk).",
      explanationImage: "/images/thyroid/thyroidarteries.png",
    },
    {
      type: "question",
      text: "Which artery is less common and supplies blood to the isthmus and lower parts of the thyroid gland?",
      options: [
        "superior thyroid artery",
        "inferior thyroid artery",
        "thyroid ima artery",
        "external carotid artery ",
      ],
      correct: 2,
      explanation:
        "The thyroid ima artery is less common and supplies blood to the isthmus and lower parts of the thyroid gland.",
      explanationImage: "/images/thyroid/thyroidarteries.png",
    },
    {
      type: "question",
      text: "Which artery arises from the external carotid artery?",
      options: [
        "superior thyroid artery",
        "inferior thyroid artery",
        "thyroid ima artery",
        "brachiocephalic artery ",
      ],
      correct: 0,
      explanation:
        "The superior thyroid artery arises from the external carotid artery.",
      explanationImage: "/images/thyroid/thyroidarteries.png",
    },
    {
      type: "question",
      text: "What is the name of this artery?",
      options: [
        "superior thyroid artery",
        "inferior thyroid artery",
        "thyroid ima artery",
        "brachiocephalic artery ",
      ],
      correct: 2,
      image: "/images/thyroid/thyroidarteriesquiz1.png",
      explanation:
        "The thyroid ima artery is less common and supplies blood to the isthmus and lower parts of the thyroid gland.",
      explanationImage: "/images/thyroid/thyroidarteries.png",
    },
    {
      type: "question",
      text: "What is the name of this artery?",
      options: [
        "external carotid artery",
        "common carotid artery",
        "subclavian artery",
        "brachiocephalic artery ",
      ],
      correct: 1,
      image: "/images/thyroid/thyroidarteriesquiz2.png",
      explanation:
        "The common carotid artery gives rise to the external carotid artery, which in turn gives rise to the superior thyroid artery.",
      explanationImage: "/images/thyroid/thyroidarteries.png",
    },
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Thyroid Venous drainage</h3>
      <p>The thyroid gland is drained via three pairs of veins:</p>       
      <p>
     1. The <strong>superior thyroid vein</strong> drains blood from the upper part of the thyroid gland and empties into the internal jugular vein.
      </p>
      <p>
      2. The <strong>middle thyroid vein</strong> drains blood from the middle part of the thyroid gland and also empties into the internal jugular vein.
      </p>
      <p>
      3. The <strong>inferior thyroid vein</strong> drains blood from the lower part of the thyroid gland and empties into the brachiocephalic vein.
      </p>
      <p>
      4. The <strong>thyroid plexus of veins</strong> connects all the above mentioned veins and surround the thyroid gland, also contributes to the venous drainage of the gland.
      These veins work together to ensure efficient venous drainage of the thyroid gland, allowing deoxygenated blood to return to the heart.
      </p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Thyroid gland veins</div>
      <div class="mnemonic-content">
      <ul>
      <li>Internal jugular vein => <b class="purple-term">Superior thyroid vein</b> => Upper part of thyroid gland</li>
      <li>Internal jugular vein => <b class="purple-term">Middle thyroid vein</b> => Middle part of thyroid gland</li>
      <li>Brachiocephalic vein => <b class="purple-term">Inferior thyroid vein</b> => Lower part of thyroid gland</li>
      <li><b class="purple-term">Thyroid plexus of veins</b> => connects all the thyroid veins and surrounds the gland</li>
      </ul>
      </div></div>
      `,
      image: "/images/thyroid/thyroidveins.png",
    },
    {
      type: "question",
      text: "Which vein drains blood from the upper part of the thyroid gland?",
      options: [
        "superior thyroid vein",
        "middle thyroid vein",
        "inferior thyroid vein",
        "brachiocephalic vein ",
      ],
      correct: 0,
      explanation:
        "The superior thyroid vein drains blood from the upper part of the thyroid gland.",
      explanationImage: "/images/thyroid/thyroidveins.png",
    },
    {
      type: "question",
      text: "Which vein drains blood from the middle part of the thyroid gland?",
      options: [
        "superior thyroid vein",
        "middle thyroid vein",
        "inferior thyroid vein",
        "brachiocephalic vein ",
      ],
      correct: 1,
      explanation:
        "The middle thyroid vein drains blood from the middle part of the thyroid gland.",
      explanationImage: "/images/thyroid/thyroidveins.png",
    },
    {
      type: "question",
      text: "From which vein does the inferior thyroid vein drain blood?",
      options: [
        "internal jugular vein",
        "middle thyroid vein",
        "brachiocephalic vein",
        "superior thyroid vein",
      ],
      correct: 2,
      explanation:
        "The inferior thyroid vein drains blood into the brachiocephalic vein.",
      explanationImage: "/images/thyroid/thyroidveins.png",
    },
    {
      type: "question",
      text: "What is the function of the thyroid plexus of veins?",
      options: [
        "Connects all thyroid veins and surrounds the gland",
        "Supplies blood to the thyroid gland",
        "Drains blood from the upper part of the thyroid gland",
        "Drains blood from the lower part of the thyroid gland",
      ],
      correct: 0,
      explanation:
        "The thyroid plexus of veins connects all the thyroid veins and surrounds the gland, contributing to its venous drainage.",
      explanationImage: "/images/thyroid/thyroidveins.png",
    },
    {
      type: "question",
      text: "What is the name of this vein?",
      options: [
        "inferior thyroid vein",
        "subclavian vein",
        "internal jugular vein",
        "brachiocephalic vein ",
      ],
      correct: 3,
      image: "/images/thyroid/thyroidveinsquiz1.png",
      explanation:
        "The brachiocephalic vein receives blood from the inferior thyroid vein.",
      explanationImage: "/images/thyroid/thyroidveins.png",
    },
    {
      type: "question",
      text: "What is the name of this vein?",
      options: [
        "inferior thyroid vein",
        "subclavian vein",
        "internal jugular vein",
        "brachiocephalic vein ",
      ],
      correct: 2,
      image: "/images/thyroid/thyroidveinsquiz2.png",
      explanation:
        "The brachiocephalic vein receives blood from the inferior thyroid vein.",
      explanationImage: "/images/thyroid/thyroidveins.png",
    },
    {
      type: "text",
      title: "Anatomy",
      content: `<h3>Thyroid innervation</h3>
      <p>The thyroid gland receives its autonomic innervation from the <strong>thyroid plexus</strong>,
        which is formed by branches of the <strong>superior, middle, and inferior cervical ganglia</strong> of the sympathetic trunk.
        </p>
      <p>The sympathetic innervation from the thyroid plexus helps regulate blood flow to the thyroid gland and influences the release of thyroid hormones.
      </p>
      <p>In addition to sympathetic innervation, the thyroid gland also receives parasympathetic innervation from the <strong>vagus nerve (cranial nerve X)</strong>.
       The parasympathetic fibers help regulate various functions of the thyroid gland, including hormone secretion.
      </p>
      <p>Together, the sympathetic and parasympathetic innervation of the thyroid gland play a crucial role in maintaining its proper function and regulating thyroid hormone levels in the body.
      </p>

      <div class="mnemonic-card">
        <div class="mnemonic-title">Thyroid gland innervation</div>
      <div class="mnemonic-content">
      Autonomic innervation from the <b class="purple-term">thyroid plexus</b>:
      <ul>
      <li>
        <strong>Sympathetic</strong> innervation from the <b class="purple-term">superior, middle, and inferior cervical ganglia</b> of the sympathetic trunk => regulates blood flow and hormone release.   
      </li>
      <li>
        <strong>Parasympathetic</strong> innervation from the <b class="purple-term">vagus nerve (cranial nerve X)</b> => regulates hormone secretion.
      </li>
      </ul>
      </div></div>
      `,
      image: "/images/thyroid/thyroidnerves.png",
    },
    {
      type: "question",
      text: "What is the name of this nerve?",
      options: [
        "inferior thyroid nerve",
        "vagus nerve",
        "internal jugular nerve",
        "brachiocephalic nerve ",
      ],
      correct: 1,
      image: "/images/thyroid/thyroidnervequiz1.png",
      explanation:
        "The vagus nerve (cranial nerve X) provides parasympathetic innervation to the thyroid gland.",
      explanationImage: "/images/thyroid/thyroidnerves.png",
    },
    {
      type: "question",
      text: "What is the name of this nerve?",
      options: [
        "superior laryngeal nerve",
        "internal laryngeal nerve",
        "external laryngeal nerve",
        "inferior laryngeal nerve",
      ],
      correct: 0,
      image: "/images/thyroid/thyroidnervequiz3.png",
      explanation:
        "The superior laryngeal nerve branches from the vagus nerve and innervates the thyroid gland.",
      explanationImage: "/images/thyroid/thyroidnerves.png",
    },
    {
      type: "question",
      text: "What is the name of this nerve?",
      options: [
        "superior laryngeal nerve",
        "internal laryngeal nerve",
        "external laryngeal nerve",
        "inferior laryngeal nerve",
      ],
      correct: 3,
      image: "/images/thyroid/thyroidnervequiz4.png",
      explanation:
        "The vagus nerve (cranial nerve X) provides parasympathetic innervation to the thyroid gland.",
      explanationImage: "/images/thyroid/thyroidnerves.png",
    },
    {
      type: "question",
      text: "What is the name of this nerve?",
      options: [
        "external laryngeal nerve",
        "inferior laryngeal nerve",
        "recurrent laryngeal nerve",
        "vagus nerve",
      ],
      correct: 2,
      image: "/images/thyroid/thyroidnervequiz2.png",
      explanation:
        "The recurrent laryngeal nerve branches from the vagus nerve and innervates the thyroid gland.",
      explanationImage: "/images/thyroid/thyroidnerves.png",
    },
  ],
};
